import { useQuery } from '@tanstack/react-query';

import { indexPortalSystems, PortalSystemOption, portalSystemQueryKeys } from 'services/api/portalSystems';

// Stable identity for the unresolved state — avoids creating a new [] on every render,
// which would invalidate downstream memos that depend on the result.
const EMPTY_SYSTEMS: PortalSystemOption[] = [];

// Last-resort label for a system the registry doesn't know (or before it loads / after
// a failed fetch): capitalize the first letter only — deliberately NOT lib/util's
// whole-word capitalize, so a hyphenated identifier gets a single leading capital —
// and deliberately private: labeling must go through the registry, and an exported
// lookalike is exactly how hard-coded label maps creep back in.
function fallbackLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// The portal registry (OLIO-3072), fetched once and held for the session — gcTime as
// well as staleTime, because without it React Query drops the cache entry five minutes
// after the last consumer unmounts and the next portal screen refetches behind a flash
// of fallback labels. `label` resolves a source_system to its display name ("fauxlio" →
// "Faux-Port"), falling back to naive capitalization while the registry loads or for a
// system it doesn't know — a chip must never render blank. A surface that goes BLANK
// without the registry (the admin settings tabs) must check isError, not render nothing.
export function usePortalSystems() {
  const query = useQuery({
    queryKey: portalSystemQueryKeys.base,
    queryFn: ({ signal }) => indexPortalSystems(signal),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const systems = query.data ?? EMPTY_SYSTEMS;
  // A plain closure over a ~4-row list — no memoized Map: no consumer puts `label` in
  // a dependency array, and the lookup is trivially cheap at this size.
  const label = (systemName: string) =>
    systems.find((system) => system.systemName === systemName)?.displayName ?? fallbackLabel(systemName);

  return { systems, label, isPending: query.isPending, isError: query.isError };
}
